import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { ProgressBar } from 'primereact/progressbar';
import { Card } from 'primereact/card';
import { Slider } from 'primereact/slider';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Tooltip } from "primereact/tooltip";
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Dropdown } from 'primereact/dropdown';
import PostsService from '../services/PostsService';
import DashboardStats from './DashboardStats';
import PostActions from './PostActions';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { InputTextarea } from 'primereact/inputtextarea';

const AnalysisModal = ({ visible, onHide, analyzeParams, setAnalyzeParams, onAnalyze }) => {
  const exclusionZoneRef = useRef(null);

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header="Analysis Parameters"
      style={{ width: '50vw' }}
    >
      <div className="p-fluid">
        <div className="field">
          <label htmlFor="algorithm">Algorithm</label>
          <Dropdown
            id="algorithm"
            value={analyzeParams.algorithm}
            options={[
              {label: 'InfoMap', value: 'infomap'},
              //{label: 'Louvain', value: 'louvain'}
            ]}
            onChange={(e) => setAnalyzeParams({...analyzeParams, algorithm: e.value})}
            tooltip="Controls how the feedback is categorized"
          />
        </div>

        <div className="field">
          <label htmlFor="edgeKNN">Graph Edge Formation K Nearest Neighbors</label>
          <InputNumber
            id="edgeKNN"
            value={analyzeParams.graphEdgeFormationKNearestNeighbors}
            onValueChange={(e) => setAnalyzeParams({...analyzeParams, graphEdgeFormationKNearestNeighbors: e.value})}
            tooltip="Controls the connectedness of the graph (10 will connect only the top 10 most similar posts, a higher number may work with Louvain but for InfoMap it may create a small number of top level Domains)"
          />
        </div>

        <div className="field">
          <label htmlFor="forceDomainReLabel">Force Domain Re-label</label>
          <Dropdown
            id="forceDomainReLabel"
            value={analyzeParams.forceDomainReLabel}
            options={[
              {label: 'No', value: false},
              {label: 'Yes', value: true}
            ]}
            onChange={(e) => setAnalyzeParams({...analyzeParams, forceDomainReLabel: e.value})}
            tooltip="Whether to force the Domain to be relabeled. By default re-labeling happens when analysis is done after crawl. There's a cost to relabeling."
          />
        </div>

        <div className="field">
          <label htmlFor="exclusionZone">Exclusion Zone Size: {analyzeParams.exclusionZoneSize}</label>
          <Slider
            className="exclusion-zone-slider"
            ref={exclusionZoneRef}
            id="exclusionZone"
            value={analyzeParams.exclusionZoneSize}
            onChange={(e) => setAnalyzeParams({...analyzeParams, exclusionZoneSize: e.value})}
            min={0}
            max={1}
            step={0.05}
          />
          <Tooltip target=".exclusion-zone-slider" content="Controls how many posts to exclude based on Non Actionable posts in the past (0-1, bigger number excludes more but can exclude good posts)" />
        </div>

        <div className="field">
          <label htmlFor="minTopicCount">Min Topic Count to Label</label>
          <InputNumber
            id="minTopicCount"
            value={analyzeParams.minTopicCountToLabel}
            onValueChange={(e) => setAnalyzeParams({...analyzeParams, minTopicCountToLabel: e.value})}
            tooltip="Controls when a label will be applied to the Topic. There's cost to labeling. (10 means a topic is only labeled if it has at least 10 posts)"
          />
        </div>

        <div className="field">
          <label htmlFor="similarityThreshold">Graph Edge Formation Similarity Threshold</label>
          <InputNumber
            id="similarityThreshold"
            value={analyzeParams.graphEdgeFormationSimilarityThreshold}
            onValueChange={(e) => setAnalyzeParams({...analyzeParams, graphEdgeFormationSimilarityThreshold: e.value})}
            min={0}
            max={1}
            step={0.1}
            tooltip="Controls the connectedness of the graph (0-1, 0 is fully connected, 1 will only connect posts that are exactly the same)"
          />
        </div>

        <div className="field">
          <label htmlFor="similarityKNN">Graph Similarity Index K Nearest Neighbors</label>
          <InputNumber
            id="similarityKNN"
            value={analyzeParams.graphSimilarityIndexKNearestNeighbors}
            onValueChange={(e) => setAnalyzeParams({...analyzeParams, graphSimilarityIndexKNearestNeighbors: e.value})}
            tooltip="Controls approximation to form the graph, only the K-nearest similar nodes are considered (1000 will calculate only the top 1000 most similar posts, small number is more performant but less accurate)"
          />
        </div>

        <Button
          label="Start Analysis"
          onClick={() => {
            onHide();
            onAnalyze();
          }}
        />
      </div>
    </Dialog>
  );
};

const CrawlModal = ({ visible, onHide, crawlParams, setCrawlParams, onCrawl }) => {
  const maxPostsToCrawlRef = useRef(null);
  const maxPostsToLabelRef = useRef(null);

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header="Crawl Parameters"
      style={{ width: '50vw' }}
    >
      <div className="p-fluid">
        <div className="field">
          <label htmlFor="maxPostsToCrawl">Maximum Posts to Crawl: {crawlParams.maxPostsToCrawl}</label>
          <Slider
            className="max-posts-to-crawl-slider"
            ref={maxPostsToCrawlRef}
            id="maxPostsToCrawl"
            value={crawlParams.maxPostsToCrawl}
            onChange={(e) => setCrawlParams({...crawlParams, maxPostsToCrawl: e.value})}
            min={1}
            max={1000}
            step={1}
          />
          <Tooltip target=".max-posts-to-crawl-slider" content="Controls how many posts to crawl (1-1000, higher numbers will take longer and cost more)" />
        </div>

        <div className="field">
          <label htmlFor="maxPostsToLabel">Maximum Posts to Label: {crawlParams.maxPostsToLabel}</label>
          <Slider
            className="max-posts-to-label-slider"
            ref={maxPostsToLabelRef}
            id="maxPostsToLabel"
            value={crawlParams.maxPostsToLabel}
            onChange={(e) => setCrawlParams({...crawlParams, maxPostsToLabel: e.value})}
            min={1}
            max={1000}
            step={1}
          />
          <Tooltip target=".max-posts-to-label-slider" content="Controls how many posts to label after crawling using LLM (1-1000, higher numbers will take longer and cost more)" />
        </div>

        <div className="field">
          <label htmlFor="crawlSources">Sources to Crawl</label>
          <div className="flex gap-2">
            <div className="flex align-items-center">
              <Checkbox
                inputId="redditSource"
                checked={crawlParams.sources.includes('reddit')}
                onChange={(e) => {
                  const newSources = e.checked 
                    ? [...new Set([...crawlParams.sources, 'reddit'])]
                    : crawlParams.sources.filter(s => s !== 'reddit');
                  setCrawlParams({...crawlParams, sources: newSources});
                }}
              />
              <label htmlFor="redditSource" className="ml-2">Reddit</label>
            </div>
            <div className="flex align-items-center">
              <Checkbox
                inputId="githubSource"
                checked={crawlParams.sources.includes('github')}
                onChange={(e) => {
                  const newSources = e.checked 
                    ? [...crawlParams.sources, 'github']
                    : crawlParams.sources.filter(s => s !== 'github');
                  setCrawlParams({...crawlParams, sources: newSources});
                }}
              />
              <label htmlFor="githubSource" className="ml-2">GitHub</label>
            </div>
          </div>
        </div>

        <Button
          label="Start Crawl"
          onClick={() => {
            if (window.confirm('For every 250 posts, this will take ~10min and cost $0.10, are you sure you want to do this?')) {
              onHide();
              onCrawl();
            }
          }}
        />
      </div>
    </Dialog>
  );
};

const ReportModal = ({ visible, onHide, reportParams, setReportParams, onReport }) => {
  const isValid = reportParams.startDate && reportParams.endDate;

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header="Report Parameters"
      style={{ width: '50vw' }}
    >
      <div className="p-fluid">
        <div className="field">
          <label htmlFor="startDate">Start Date*</label>
          <Calendar
            id="startDate"
            value={reportParams.startDate}
            onChange={(e) => setReportParams({...reportParams, startDate: e.value})}
            showTime
            hourFormat="24"
            dateFormat="mm/dd/yy"
            placeholder="Select Start Date"
            className={!reportParams.startDate ? 'p-invalid' : ''}
          />
          {!reportParams.startDate && <small className="p-error">Start date is required</small>}
        </div>

        <div className="field">
          <label htmlFor="endDate">End Date*</label>
          <Calendar
            id="endDate"
            value={reportParams.endDate}
            onChange={(e) => setReportParams({...reportParams, endDate: e.value})}
            showTime
            hourFormat="24"
            dateFormat="mm/dd/yy"
            placeholder="Select End Date"
            minDate={reportParams.startDate}
            className={!reportParams.endDate ? 'p-invalid' : ''}
          />
          {!reportParams.endDate && <small className="p-error">End date is required</small>}
        </div>

        <div className="field">
          <label htmlFor="notes">Patch Note Override (Optional)</label>
          <InputTextarea
            id="notes"
            value={reportParams.notes}
            onChange={(e) => setReportParams({...reportParams, notes: e.target.value})}
            placeholder="Optional override for patch notes"
            rows={5}
            autoResize
          />
        </div>

        <Button
          label="Generate Report"
          onClick={() => {
            if (window.confirm('This will take ~1min and cost $0.10, are you sure you want to do this?')) {
              onHide();
              onReport();
            }
          }}
          disabled={!isValid}
          tooltip={!isValid ? "Please select both start and end dates" : undefined}
        />
      </div>
    </Dialog>
  );
};

const Dashboard = ({ onLogout }) => {
  const toast = useRef(null);
  const [posts, setPosts] = useState([]);
  const [dedupeClusters, setDedupeClusters] = useState({});
  const [crawlLoading, setCrawlLoading] = useState(false);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [analyzeModalOpen, setAnalyzeModalOpen] = useState(false);
  const [crawlModalOpen, setCrawlModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [globalFilter, setGlobalFilter] = useState('');
  const [stats, setStats] = useState({
    totalPosts: 0,
    unreadPosts: 0,
    starredPosts: 0,
    skippedPosts: 0,
    lastCrawledTime: null,
    lastAnalyzedTime: null
  });

  const [crawlParams, setCrawlParams] = useState({
    maxPostsToCrawl: 1000,
    maxPostsToLabel: 1000,
    sources: ['reddit', 'github']
  });

  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportParams, setReportParams] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    endDate: new Date(),
    notes: ''
  });

  const showToast = (severity, summary, detail, life=3000) => {
    toast.current.show({
      severity,
      summary,
      detail,
      life: life
    });
  };

  const fetchPosts = async () => {
    try {
      const data = await PostsService.getPosts();
      //const data = await PostsService.getPostsViaS3();
      const updatedData = data.findings
        .filter(post => post.actionable && !post.excluded_via_analysis)
        // Group by dedupe_cluster_id and get latest post from each group
        .reduce((acc, post) => {
          if (!post.dedupe_cluster_id) {
            // Keep posts without dedupe_cluster_id
            post.duplicate_count = 0;
            acc.push(post);
          } else {
            // Find existing post with same dedupe_cluster_id
            const existingIndex = acc.findIndex(p => p.dedupe_cluster_id === post.dedupe_cluster_id);
            if (existingIndex === -1) {
              // Add if no existing post found
              post.duplicate_count = 0;
              acc.push(post);
            } else if (new Date(post.post_date) > new Date(acc[existingIndex].post_date)) {
              // Replace if current post is newer
              post.duplicate_count = acc[existingIndex].duplicate_count + 1;
              if (acc[existingIndex].status !== "new" && post.status === "new") {
                post.status = "new_seen";
              }
              acc[existingIndex] = post;
            } else {
              acc[existingIndex].duplicate_count = acc[existingIndex].duplicate_count + 1;
              if (acc[existingIndex].status === "new" && post.status !== "new") {
                acc[existingIndex].status = "new_seen";
              }
            }
          }
          return acc;
        }, [])
        .map(post => ({
          ...post,
          post_date: new Date(post.post_date),
          cluster_id: parseInt(post.cluster_id),
          parent_cluster_id: parseInt(post.parent_cluster_id), 
          impact: parseInt(post.impact),
          comment_count: parseInt(post.comment_count),
          total_comment_score: parseInt(post.total_comment_score)
        }));
      setPosts(updatedData);
      
      // First, create a map of dedupe clusters
      const dedupeClusters = {};
      data.findings.forEach(post => {
          if (post.dedupe_cluster_id) {
            if (!dedupeClusters[post.dedupe_cluster_id]) {
              dedupeClusters[post.dedupe_cluster_id] = [];
            }
            dedupeClusters[post.dedupe_cluster_id].push({
              ...post,
              post_date: new Date(post.post_date),
              cluster_id: parseInt(post.cluster_id),
              parent_cluster_id: parseInt(post.parent_cluster_id),
              impact: parseInt(post.impact),
              comment_count: parseInt(post.comment_count),
              total_comment_score: parseInt(post.total_comment_score)
            });
          }
        });
      setDedupeClusters(dedupeClusters);

      if (data.last_crawled_time) {
        data.last_crawled_time = new Date(data.last_crawled_time).toLocaleString('en-US');
      }

      if (data.last_analyzed_time) {
        data.last_analyzed_time = new Date(data.last_analyzed_time).toLocaleString('en-US');
      }
      
      setStats({
        totalPosts: data.findings.length,
        unreadPosts: updatedData.filter(post => post.status === "new" || post.status === "new_seen").length,
        starredPosts: updatedData.filter(post => post.status === "acknowledged" || post.status === "work_in_progress").length,
        skippedPosts: data.findings.length - updatedData.length,
        lastCrawledTime: data.last_crawled_time,
        lastAnalyzedTime: data.last_analyzed_time
      });
    } catch (error) {
      showToast('error', 'Error', 'Error fetching posts');
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const handleCrawl = async () => {
    setCrawlLoading(true);
    try {
      await PostsService.triggerCrawl(crawlParams);
      await fetchPosts();
      showToast('success', 'Success', 'Crawl completed successfully');
    } catch (error) {
      showToast('error', 'Error', 'Error triggering crawl');
    }
    setCrawlLoading(false);
  };

  const handleSendReport = async () => {
    setReportLoading(true);
    try {
      // Format dates to remove the 'Z' and use '+00:00' for UTC timezone
      const formatDate = (date) => {
        if (!date) return null;
        return date.toISOString().replace('Z', '+00:00');
      };

      await PostsService.triggerReport({
        start_date: formatDate(reportParams.startDate),
        end_date: formatDate(reportParams.endDate),
        notes: reportParams.notes
      });
      showToast('success', 'Success', 'Report sent successfully');
    } catch (error) {
      showToast('error', 'Error', 'Error triggering report');
    }
    setReportLoading(false);
  };

  const [analyzeParams, setAnalyzeParams] = useState({
    algorithm: 'infomap',
    exclusionZoneSize: 0.2,
    forceDomainReLabel: false,
    minTopicCountToLabel: 10,
    graphEdgeFormationSimilarityThreshold: 0.3,
    graphEdgeFormationKNearestNeighbors: 10,
    graphSimilarityIndexKNearestNeighbors: 1000
  });

  const handleAnalyze = async () => {
    setAnalyzeLoading(true);
    try {
      const result =await PostsService.triggerAnalyze(analyzeParams);
      await fetchPosts();
      showToast('success', 'Success', `Analysis completed successfully. Details: ${JSON.stringify(result, null, 4)}`, 30000);
    } catch (error) {
      showToast('error', 'Error', 'Error triggering analysis');
    }
    setAnalyzeLoading(false);
  };

  const dateTemplate = (rowData) => {
    return rowData.post_date.toLocaleString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
      hour: '2-digit',
      minute: 'numeric',
      second: 'numeric',
      hour12: false
    });//.replace(',','');//.replace("AM","").replace("PM","");
  };

  const wordWrap = (text, maxLength = 100) => {
    if (!text) return '';
    
    // Split text into words
    const words = text.split(' ');
    let result = [];
    let currentLine = '';

    for (let word of words) {
      // If word itself is longer than maxLength, break it up
      if (word.length > maxLength) {
        if (currentLine) {
          result.push(currentLine.trim())
          result.push(<br/>);
          currentLine = '';
        }
        // Break long word into chunks
        for (let i = 0; i < word.length; i += maxLength) {
          result.push(word.slice(i, i + maxLength));
          if (i + maxLength < word.length) {
            result.push(<br/>);
          }
        }
        continue;
      }

      // Check if adding this word would exceed maxLength
      if ((currentLine + word).length > maxLength) {
        result += currentLine.trim() + '\n';
        currentLine = word + ' ';
      } else {
        currentLine += word + ' ';
      }
    }

    // Add any remaining text
    if (currentLine) {
      result += currentLine.trim();
    }

    return result;
  };

  const titleTemplate = (rowData) => {
    return (
      <a 
        href={rowData.url} 
        target="_blank" 
        rel="noopener noreferrer"
        title={rowData.title}
        style={{
          display: 'block',
          maxWidth: '100%',
          textOverflow: 'ellipsis',
          overflow: 'hidden'
        }}
      >
        {rowData.source === "reddit" ? (
          <i className="pi pi-reddit" style={{marginRight: '5px'}}></i>
        ) : rowData.source === "github" ? (
          <i className="pi pi-github" style={{marginRight: '5px'}}></i>
        ) : (
          <i className="pi pi-question" style={{marginRight: '5px'}}></i>
        )}
        {wordWrap(rowData.title, 35)} ({String(rowData.comment_count)}|{String(rowData.total_comment_score)})
      </a>
    );
  };

  const summaryTemplate = (rowData) => {
    return (
      <div
        style={{
          cursor: 'pointer'
        }}
        onMouseOver={(e) => {
          e.target.style.fontWeight = 'bold';
        }}
        onMouseOut={(e) => {
          e.target.style.fontWeight = 'normal';
        }}
        onClick={() => {
          setSelectedPost(rowData);
        }}
      >
        <i className="pi pi-window-maximize" style={{marginRight: '5px'}}></i>{wordWrap(rowData.summary, 70)}
      </div>
    );
  };

  const categoriesTemplate = (rowData) => {
    return <p>{rowData.categories?.map((line, i) => (
      <React.Fragment key={i}>
        {i > 0 && <br />}
        {`${line}${i < rowData.categories.length - 1 ? ',' : ''}`}
      </React.Fragment>
    ))}</p>
  };

  const actionsTemplate = (rowData) => {
    return <PostActions post={rowData} onActionComplete={fetchPosts} />;
  };

  const handleStatusChange = async (id, source, newStatus) => {
    try {
      await PostsService.updatePost(id, {
        source: source,
        status: newStatus
      });
      setPosts(prevPosts => prevPosts.map(post => 
        post.id === id ? {...post, status: newStatus} : {...post}
      ));
      setStats(prevStats => {
        const updatedPosts = posts.map(post =>
          post.id === id ? {...post, status: newStatus} : {...post}
        );
        return {
          ...prevStats,
          unreadPosts: updatedPosts.filter(post => post.status === "new" || post.status === "new_seen").length,
          starredPosts: updatedPosts.filter(post => post.status === "acknowledged" || post.status === "work_in_progress").length
        };
      });
      showToast('success', 'Success', 'Status updated successfully');
    } catch (error) {
      console.error(error);
      showToast('error', 'Error', 'Failed to update status');
    }
  };

  const handleUpdateStatusByClusterID = async (cluster_id) => {
    try {
      const res = await PostsService.updateStatusByClusterId(cluster_id, {
        status: "not_actionable"
      });
      setPosts(prevPosts => prevPosts.map(post => 
        post.cluster_id === cluster_id && (post.status === "new" || post.status === "new_seen") 
          ? {...post, status: "not_actionable"} 
          : {...post}
      ));
      setStats(prevStats => {
        const updatedPosts = posts.map(post =>
          post.cluster_id === cluster_id && (post.status === "new" || post.status === "new_seen") 
            ? {...post, status: "not_actionable"} 
            : {...post}
        );
        return {
          ...prevStats,
          unreadPosts: updatedPosts.filter(post => post.status === "new" || post.status === "new_seen").length,
          starredPosts: updatedPosts.filter(post => post.status === "acknowledged" || post.status === "work_in_progress").length
        };
      });
      showToast('success', 'Success', `${res.num_updated} feedback marked as not actionable`, 5000);
    } catch (error) {
      console.error(error);
      showToast('error', 'Error', 'Failed to update status');
    }
  };

  return (
    <div className="p-4">
      <Toast ref={toast} />
      <div className="flex justify-content-between align-items-center mb-4">
        <DashboardStats stats={stats} />
        <Button 
          label="Logout"
          icon="pi pi-sign-out"
          severity="secondary"
          onClick={onLogout}
        />
      </div>
      <div className="flex gap-2 mb-4">
        <Button 
          label={crawlLoading ? 'Crawling...' : 'Crawl'}
          onClick={() => setCrawlModalOpen(true)}
          loading={crawlLoading}
        />
        <Button 
          label={analyzeLoading ? 'Categorizing...' : 'Categorize'}
          onClick={() => setAnalyzeModalOpen(true)}
          loading={analyzeLoading}
        />
        <Button 
          label={reportLoading ? 'Sending...' : 'Report'}
          onClick={() => setReportModalOpen(true)}
          loading={reportLoading}
        />
      </div>

      <CrawlModal 
        visible={crawlModalOpen}
        onHide={() => setCrawlModalOpen(false)}
        crawlParams={crawlParams}
        setCrawlParams={setCrawlParams}
        onCrawl={handleCrawl}
      />

      <AnalysisModal 
        visible={analyzeModalOpen}
        onHide={() => setAnalyzeModalOpen(false)}
        analyzeParams={analyzeParams}
        setAnalyzeParams={setAnalyzeParams}
        onAnalyze={handleAnalyze}
      />

      <ReportModal
        visible={reportModalOpen}
        onHide={() => setReportModalOpen(false)}
        reportParams={reportParams}
        setReportParams={setReportParams}
        onReport={handleSendReport}
      />

      <Card>
        <div style={{ width: "100%", margin: "auto" }}>
          <div className="mb-3 flex justify-content-between align-items-center">
            <h2 className="m-0">Feedback</h2>
            <span className="p-input-icon-right">
              <InputText 
                value={globalFilter}
                placeholder="Global Search..." 
                onChange={(e) => setGlobalFilter(e.target.value)}
                style={{"border-radius": '6px'}}
              />
            </span>
          </div>

          
          <DataTable
            value={posts}
            responsiveLayout="scroll"
            style={{ tableLayout: "fixed", width: "100%" }}
            paginator
            rows={25}
            rowsPerPageOptions={[10, 25, 100, 500, 5000]}
            sortMode="multiple"
            loading={crawlLoading || analyzeLoading}
            dataKey="id"
            filterDisplay="menu"
            globalFilter={globalFilter}
            globalFilterFields={['title', 'summary', 'url', 'status', 'parent_cluster_label', 'cluster_label']}
            //resizableColumns
            //columnResizeMode="fit"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
          >
            <Column 
              field="post_date" 
              header="Post Date" 
              sortable 
              filter 
              dataType="date"
              filterField="post_date"
              body={dateTemplate}
              style={{width: "1%"}}
              filterElement={(options) => (
                <Calendar
                  value={options.value}
                  onChange={(e) => options.filterCallback(e.value)}
                  dateFormat="mm/dd/yy"
                  placeholder="mm/dd/yyyy"
                  mask="99/99/9999"
                />
              )}
            />
            <Column 
              field="title" 
              header="Title" 
              sortable 
              filter
              filterPlaceholder="Search titles..."
              sortField="comment_count"
              body={titleTemplate} 
              style={{ 
                width: "25%", 
                minWidth: "200px",
                maxWidth: "25%",
                wordWrap: "break-word", whiteSpace: "normal",
                overflow: "hidden"
              }}  
            />
            <Column 
              field="summary" 
              header="Summary" 
              sortable 
              filter
              filterPlaceholder="Search summaries..."
              body={summaryTemplate} 
              style={{ width: "50%", minWidth: "300px",
                wordWrap: "break-word", whiteSpace: "normal",
                overflow: 'hidden', textOverflow: 'ellipsis', transition: 'opacity 0.2s, font-weight 0.2s' }}  
            />
            { /*<Column 
              field="impact" 
              header="Impact" 
              dataType="numeric" 
              sortable 
              filter 
              style={{width: '1%'}}
              body={(rowData) => (
                <div style={{position: 'relative'}}>
                  <ProgressBar 
                    value={(rowData.impact / 10) * 100}
                    showValue={false}
                    style={{height: '20px', width: '100%'}}
                  />
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: rowData.impact > 8 ? '#FF0000' : 
                           rowData.impact > 6 ? '#FFA500' :
                           rowData.impact > 4 ? '#FFFF00' : '#FFFFFF',
                    fontWeight: 'bold'
                  }}>
                    {rowData.impact}
                  </div>
                </div>
              )}
            />*/}
            <Column 
              field="status" 
              header="Status" 
              sortable 
              filter
              filterMatchMode="equals"
              filterElement={options => (
                <Dropdown
                  value={options.value}
                  options={[
                    {label: 'New', value: 'new'},
                    {label: 'New (Seen Before)', value: 'new_seen'},
                    {label: 'Acknowledged', value: 'acknowledged'},
                    //{label: 'Work in Progress', value: 'work_in_progress'}, 
                    {label: 'Closed', value: 'closed'},
                    {label: 'Not Actionable', value: 'not_actionable'}
                  ]}
                  onChange={(e) => options.filterCallback(e.value)}
                  placeholder="Select Status"
                  className="p-column-filter"
                />
              )}
              style={{width: '1%'}}
              body={(rowData) => {
                let severity;
                switch(rowData.status) {
                  case 'new':
                    severity = 'info';
                    break;
                  case 'acknowledged': 
                    severity = 'success';
                    break;
                  case 'work_in_progress':
                    severity = 'warning';
                    break;
                  case 'closed':
                    severity = 'secondary';
                    break;
                  case 'not_actionable':
                    severity = 'danger';
                    break;
                  default:
                    severity = 'contrast';
                }
                let options = [
                  {label: 'New', value: 'new'},
                  {label: 'Acknowledged', value: 'acknowledged'},
                  //{label: 'Work in Progress', value: 'work_in_progress'},
                  {label: 'Closed', value: 'closed'},
                  {label: 'Not Actionable', value: 'not_actionable'}
                ]
                if (rowData.status === 'new_seen') {
                  options.splice(1, 0, {label: 'New (Seen Before)', value: 'new_seen'});
                  options.shift();
                }
                return (
                  <div className="flex align-items-center">
                    <Dropdown
                      value={rowData.status}
                      options={options}
                      onChange={(e) => handleStatusChange(rowData.id, rowData.source, e.value)}
                      className={`p-inputtext-sm status-${severity}`}
                      style={{minWidth: '150px'}}
                    />
                  </div>
                );
              }}
            />
            <Column field="parent_cluster_label" header="Domain" dataType="numeric" sortable filter style={{width: '10%'}}
              filterElement={(options) => {
                const clusterCounts = posts.reduce((acc, post) => {
                  acc[post.parent_cluster_label] = (acc[post.parent_cluster_label] || 0) + 1;
                  return acc;
                }, {});

                // Create sorted options with counts
                const sortedOptions = [...new Set(posts.map(post => post.parent_cluster_label))]
                  .sort((a, b) => clusterCounts[b] - clusterCounts[a])
                  .map(id => ({
                    label: `${id} (${clusterCounts[id]} of ${posts.length})`,
                    value: id
                  }));

                return (
                  <Dropdown
                    value={options.value}
                    options={sortedOptions}
                    onChange={(e) => options.filterCallback(e.value)}
                    placeholder="Select Parent Cluster"
                    className="p-column-filter"
                  />
                );
              }}
            />
            <Column field="cluster_label" header="Topic" dataType="numeric" sortable filter style={{width: '10%'}}
              filterElement={(options) => {
                // Get filtered posts based on other active filters
                const filteredPosts = options.filters ? 
                  posts.filter(post => {
                    for (let field in options.filters) {
                      if (field !== 'cluster_label' && options.filters[field].value) {
                        if (post[field] !== options.filters[field].value) {
                          return false;
                        }
                      }
                    }
                    return true;
                  }) : posts;

                // Get counts for each cluster_label from filtered posts
                const clusterCounts = filteredPosts.reduce((acc, post) => {
                  if (post.cluster_label) {
                    acc[post.cluster_label] = (acc[post.cluster_label] || 0) + 1;
                  }
                  return acc;
                }, {});

                // Get total count of filtered posts with clusters
                const totalClusteredPosts = Object.values(clusterCounts).reduce((sum, count) => sum + count, 0);

                // Create sorted options with counts
                const sortedOptions = [...new Set(filteredPosts.map(post => post.cluster_label))]
                  .filter(label => label != null)
                  .sort((a, b) => clusterCounts[b] - clusterCounts[a])
                  .map(id => ({
                    label: `${id} (${clusterCounts[id]} of ${totalClusteredPosts})`,
                    value: id
                  }));

                return (
                  <Dropdown
                    value={options.value}
                    options={sortedOptions}
                    onChange={(e) => options.filterCallback(e.value)}
                    placeholder="Select Cluster"
                    className="p-column-filter"
                  />
                );
              }}
              body={(rowData) => (
                <div className="flex align-items-center gap-2">
                  <span>{rowData.cluster_label}</span>
                  {(rowData.cluster_label != null && rowData.cluster_label != undefined) && (
                    <Button
                      icon="pi pi-trash"
                      text
                      severity="danger"
                      onClick={() => {
                        if (window.confirm(`This will mark all posts in cluster ${rowData.cluster_label} as not actionable, are you sure you want to do this?`)) {
                          handleUpdateStatusByClusterID(rowData.cluster_id)
                        }
                      }}
                    />
                  )}
                </div>
              )}
            />
            <Column 
              field="source" 
              header="Source" 
              sortable
              filter
              filterMatchMode="equals"
              body={(rowData) => (
                <div className="flex align-items-center gap-2">
                  <span>{rowData.source}</span>
                </div>
              )}
            />
            <Column 
              field="duplicate_count" 
              header="Dupe Count" 
              sortable 
              body={(rowData) => (
                <div className="flex align-items-center gap-2">
                  <span>{rowData.duplicate_count}</span>
                </div>
              )}
            />
          </DataTable>
        </div>
      </Card>
      <Dialog
        visible={selectedPost !== null}
        onHide={() => setSelectedPost(null)}
        header={selectedPost?.title}
        style={{ width: '80vw' }}
        modal={true}
        dismissableMask={true}
      >
        {selectedPost && (
          <div className="p-4">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p><strong>Categories:</strong> {selectedPost.categories?.join(', ')}</p>
                <p><strong>Impact:</strong> {selectedPost.impact}</p>
                <p><strong>Difficulty:</strong> {selectedPost.difficulty}</p>
              </div>
              <div>
                <p><strong>Status:</strong> {selectedPost.status}</p>
                <p><strong>Comments:</strong> {selectedPost.comment_count}</p>
                <p><strong>Comment Score:</strong> {selectedPost.total_comment_score}</p>
              </div>
            </div>
            <hr className="my-4" />
            <h3 className="text-xl font-bold mb-2">Summary</h3>
            <p className="mb-4">{selectedPost.summary}</p>
            
            <h3 className="text-xl font-bold mb-2">Full Content</h3>
            <p>{selectedPost.description?.split('\n').map((line, i) => (
              <React.Fragment key={i}>
                {line.startsWith('Content:') ? <>{line.replace('Content: ', '')}<br /></> : 
                 line.startsWith('Comments:') ? <h3 className="text-xl font-bold mb-2">{line}</h3> : 
                 line.startsWith('Title:') ? <></> : <pre style={{margin: 0, fontFamily: 'inherit', whiteSpace: 'pre-wrap'}}>{line}</pre>}
              </React.Fragment>
            ))}</p>

{selectedPost.dedupe_cluster_id && dedupeClusters[selectedPost.dedupe_cluster_id] && (
              <>
                <h3 className="text-xl font-bold mb-2">Duplicate Posts</h3>
                <DataTable
                  value={dedupeClusters[selectedPost.dedupe_cluster_id].filter(post => post.id !== selectedPost.id)}
                  style={{ marginBottom: '1rem' }}
                  size="small"
                >
                  <Column
                    field="post_date"
                    header="Post Date"
                    body={(rowData) => rowData.post_date.toLocaleString('en-US', {
                      month: 'numeric',
                      day: 'numeric',
                      year: '2-digit',
                      hour: '2-digit',
                      minute: 'numeric',
                      hour12: false
                    })}
                    sortable
                    style={{ width: '150px' }}
                  />
                  <Column
                    field="title"
                    header="Title"
                    sortable
                    body={(rowData) => (
                      <a 
                        href={rowData.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#2196F3', textDecoration: 'none' }}
                      >
                        {rowData.source === "reddit" ? (
                          <i className="pi pi-reddit" style={{marginRight: '5px'}}></i>
                        ) : rowData.source === "github" ? (
                          <i className="pi pi-github" style={{marginRight: '5px'}}></i>
                        ) : (
                          <i className="pi pi-question" style={{marginRight: '5px'}}></i>
                        )}
                        {rowData.title}
                      </a>
                    )}
                  />
                  <Column
                    field="status"
                    header="Status"
                    sortable
                    style={{ width: '150px' }}
                  />
                  <Column
                    field="comment_count"
                    header="Comments"
                    sortable
                    style={{ width: '100px' }}
                  />
                  <Column
                    field="total_comment_score"
                    header="Score"
                    sortable
                    style={{ width: '100px' }}
                  />
                  <Column
                    field="source"
                    header="Source"
                    sortable
                    style={{ width: '100px' }}
                  />
                </DataTable>
              </>
            )}
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default Dashboard;
